import {Button, CircularProgress, Typography} from "@mui/material";
import {Box, SxProps} from "@mui/system";
import React, {ChangeEvent, FormEvent, useCallback, useEffect, useState} from "react";
import {useChangePasswordMutation} from "../app/services/api";
import LoginTextField from "../components/LoginTextField/LoginTextField";
import {SUPPORT_URL} from "../components/ContactText";
import IconButton from "@mui/material/IconButton";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Visibility from "@mui/icons-material/Visibility";
import TokenBox from "../components/TokenBox/TokenBox";
import {navigate} from "../app/services/routing";

const boxStyle: SxProps = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignContent: 'center',
    flex: 1
};

const buttonBoxStyle: SxProps = {
    display: "flex",
    gap: 1,

    a: {
        flex: 1,
    }
};


const passwordPattern = '\.{12,}';


const passwordHelperText =  'Minimum length 12 characters.';
const repeatPasswordHelperText = (mismatch: boolean) => mismatch ? 'Passwords don\'t match.' : 'Please re-enter your password.';

const cancelButton = () => ( <Button
    id="cancel-button"
    variant="outlined"
    color="primary"
    href={"/"}
>
    Cancel
</Button>);

const returnToLogin = () => (
    <Button
        fullWidth
        href="/"
        variant="contained"
        color="secondary"
    >
        Return to Login
    </Button>
);

const ResetPassword = () => {
    // Derive necessary info from the url
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams?.get('password_reset_token') || '';
    const otp_enabled = urlParams?.get('2fa') === 'true';
    const user =  urlParams?.get('user') || '';
    const relation =  urlParams?.get('relation') || '';

    // Define a password field and functionality dealing its visibility and validity check
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [invalidPassword, setInvalidPassword] = useState(false);

    // Define a repeat password field and functionality dealing its visibility and validity check
    const [repeatPassword, setRepeatPassword] = useState('');
    const [showRepeatPassword, setShowRepeatPassword] = useState(false);
    const [invalidRepeatPassword, setInvalidRepeatPassword] = useState(false);

    const [otpToken, setOtpToken] = useState('');
    const [invalidOTP, setInvalidOTP] = useState(false);
    const [invalidActivationToken, setInvalidActivationToken] = useState(false);

    const [currentForm, setCurrentForm] = useState("password");


    useEffect(() => {
        setInvalidOTP(false);
    }, [otpToken]);

    const onSubmit = async (ev: FormEvent) => {
        ev.preventDefault();
        if (otp_enabled) {
            setCurrentForm("token");
        } else {
            const result = await changePassword({
                new_password: password,
                new_password_verify: repeatPassword,
                token,
                user,
                relation
            });

            handlePasswordChange(result);
        }
    };

    const submitButtonText = () => otp_enabled ? 'Continue' : 'Reset Password';

    const onPasswordChange = async (ev: FormEvent) => {
        ev.preventDefault();
        if((ev.target as HTMLFormElement).checkValidity()) {
            const result  = await changePassword({
                new_password: password,
                new_password_verify: repeatPassword,
                token,
                otp_token: otpToken,
                user,
                relation
            });

            handlePasswordChange(result);
        }
    };


    const handleClickShowPassword = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        setShowPassword((show) => !show);
    }, [setShowPassword]);

    const handleClickShowRepeatPassword = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        setShowRepeatPassword((show) => !show);
    }, [setRepeatPassword]);


    const [changePassword, changePasswordResult] = useChangePasswordMutation();


    const handlePasswordChange = (result: any) => {
        if (result.error) {
            const errorCode = result.error.status as number;

            if (errorCode === 403) {
                setInvalidOTP(true);
            } else {
                setCurrentForm("error");
                switch (errorCode) {
                    case 400:
                        setInvalidActivationToken(true);
                        break;

                    default:
                        break;
                }
            }
        } else {
            setCurrentForm('confirmation');
        }
    };

    const passwordMismatch = password !== repeatPassword;

    const renderOTPBox = () => (
        <Box sx={boxStyle}>
            <Box sx={{flex: 1}}>
                <TokenBox
                    setToken={setOtpToken}
                    value={otpToken}
                    onCancel={() => navigate('/')}
                    handleSubmit={onPasswordChange}
                    error={invalidOTP}
                    signupProcess={false}
                    loading={changePasswordResult.isLoading}
                />
            </Box>
        </Box>
    );

    const renderErrorForm = () => (
        <Box sx={boxStyle}>
            <Box sx={{ flex: 1 }}>
                <Typography variant="h2" gutterBottom color="error.main">
                    Oops!
                </Typography>
                { invalidActivationToken ?
                    <>
                        <Typography gutterBottom>
                            Seems like the link that you used to reset the password is already used or it has expired.
                        </Typography>
                        <Typography gutterBottom>
                            Please request a new email to reset the password <a href={'/service/forgot-password'}> here</a>
                        </Typography>
                    </> :
                    <>
                        <Typography gutterBottom>
                            Unfortunately, we could not reset your password.
                        </Typography>
                        <Typography gutterBottom>
                            If you wish to give it another try, please press on the &lsquo;Reset Password&rsquo; button below.
                        </Typography>
                        <Typography gutterBottom>
                            Or please contact our{" "}
                            <a href={SUPPORT_URL} target="_blank" rel="noreferrer">
                                customer care and support desk{" "}
                            </a> for further assistance.
                        </Typography>
                    </>
                }

            </Box>
            <Box sx={buttonBoxStyle}>
                <Button
                    href="/"
                    variant="outlined"
                    color="primary"
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    href={'/service/forgot-password'}
                    color="secondary"
                >
                    Reset Password
                </Button>
            </Box>

        </Box>
    );

    const renderConfirmationForm = () => (
        <Box sx={boxStyle}>
            <Box sx={{ flex: 1 }}>
                <Typography variant="h2" gutterBottom>
                    Your password has been reset!
                </Typography>
                <>
                    <Typography gutterBottom>
                        You can login with your new password and please try to remember it this time 😉.
                    </Typography>
                </>

            </Box>
            {returnToLogin()}
        </Box>
    );


    // eslint-disable-next-line complexity
    const renderPasswordForm = () => (
        <Box component="form" onSubmit={onSubmit} sx={boxStyle}>
            <Box>
                <Typography variant="h2" gutterBottom>
                    Let&apos;s reset your password&hellip;
                </Typography>

                <Typography variant="body1" gutterBottom>
                    Please enter your new password twice and click on &lsquo;{submitButtonText()}&rsquo; button.
                </Typography>

                <Box>
                    <LoginTextField
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        label="New password:"
                        helperText={passwordHelperText}
                        value={password}
                        required={true}
                        error={Boolean(password) && invalidPassword}
                        onChangeHandler={(ev: ChangeEvent<HTMLInputElement>, value: string) => {
                            setPassword(value);
                            setInvalidPassword(!(ev.target.checkValidity()));
                        }}
                        InputProps={{
                            endAdornment: <IconButton
                                component="button"
                                aria-label="toggle password visibility"
                                title={showPassword ? 'Hide password' : 'Show password'}
                                onClick={handleClickShowPassword}
                                edge="end"
                            >
                                {showPassword ? <VisibilityOff/> : <Visibility/>}
                            </IconButton>
                        }}
                        inputProps={{pattern: passwordPattern}}
                    />

                    <LoginTextField
                        id="repeat-password"
                        type={showRepeatPassword ? 'text' : 'password'}
                        label="Repeat new password:"
                        helperText={repeatPasswordHelperText((Boolean(repeatPassword) && passwordMismatch))}
                        value={repeatPassword}
                        error={(Boolean(repeatPassword) && invalidRepeatPassword) || (Boolean(repeatPassword) && passwordMismatch) }
                        required={true}
                        focused={false}
                        onChangeHandler={(ev: ChangeEvent<HTMLInputElement>, value: string) => {
                            setRepeatPassword(value);
                            setInvalidRepeatPassword(!(ev.target.checkValidity()));
                        }}
                        InputProps={{
                            endAdornment: <IconButton
                                component="button"
                                aria-label="toggle password visibility"
                                title={showRepeatPassword ? 'Hide password' : 'Show password'}
                                onClick={handleClickShowRepeatPassword}
                                edge="end"
                            >
                                {showRepeatPassword ? <VisibilityOff/> : <Visibility/>}
                            </IconButton>

                        }}
                        inputProps={{pattern: passwordPattern}}
                    />
                </Box>
            </Box>
            <Box sx={{display: 'flex', gap: 1, Button: {flex: 1}, a: {flex: 1}}}>
                {cancelButton()}
                <Button
                    variant="contained"
                    color="secondary"
                    type="submit"
                    endIcon={changePasswordResult.isLoading ? <CircularProgress size={14} color={'inherit'} /> : null}
                    disabled={
                        changePasswordResult.isLoading ||
                    passwordMismatch || invalidPassword || invalidRepeatPassword || Boolean(!password)
                    }
                >
                    {otp_enabled ? 'Continue' : submitButtonText()}
                </Button>
            </Box>
        </Box>
    );

    const content = () => {
        switch (currentForm) {
            case "token":
                return renderOTPBox();

            case "error":
                return renderErrorForm();

            case "confirmation":
                return renderConfirmationForm();

            case "password":
            default:
                return renderPasswordForm();
        }
    };


    return content();
};

export default ResetPassword;
